import { useContext, useEffect, useMemo, useState } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import i18nContext from 'components/i18n-context';
import { CUSTOMER_TYPE, KYC_PROFILE, DATA_LAYER_EVENTS, USER_CUSTOMER_STATUSES } from 'components/common/constants';
import { getErrorMessageForAlert, trimTextContent } from 'services/utils';
import { getCustomerId, getCustomerType, getInternalUserId, isTokenValid } from 'services/authUtils';
import { pushDataLayerEvent } from 'services/analytics';
import { trackTelemetryEvent, TELEMETRY_EVENTS } from 'services/telemetry';
import { ROUTE_PATHS } from 'routes/constants';
import AccountTypeCard from './AccountTypeCard';
import Alert from 'uikit/Alert/Alert';
import Input from 'uikit/Input/Input';
import { ReactComponent as BriefcaseIcon } from 'assets/customerCreation/finance-briefcase.svg';
import { ReactComponent as UserOneIcon } from 'assets/customerCreation/user-one.svg';
import { ReactComponent as ComputerIcon } from 'assets/customerCreation/computer.svg';
import { ReactComponent as UserShieldIcon } from 'assets/customerCreation/user-shiled.svg';
import './ChooseAccountType.scss';

const INVITE_CODE = process.env.REACT_APP_PERSONAL_CUSTOMER_REGISTRATION_INVITATION_CODE;

// TRN-7388 — shared "Choose Your Account Type" screen: create an empty customer via the v2 registration
// endpoints, then send the user to /verification where the Sumsub/Shufti/Manual SDK collects their actual
// details. Reused at two call sites — see docs/specs/TRN-7388_customer_creation/spec.md §10.1 and §13:
//  - /verification, rendered by DashBoard.js for a logged-in customer-less user (replaces the old
//    NoOutlet(ACCOUNT_NOT_REGISTERED) placeholder).
//  - /registration/customer, rendered directly by App.js's route for an already-logged-in user adding an
//    additional account (Header/UserPanel.js's "Open an account" dropdown option) — replaces the old
//    CustomerRegistrationTypeChoosing + Personal/Business full-detail forms (now unwired legacy, see
//    Registration/Account/index.js).
// TRN-7390: all four cards now register via the unified POST /v2/registration/customer/{kyc_profile}
// endpoint (userStore.registerCustomer). Self-employed and Company Member Access are real options again
// (TRN-7457 had removed them as "coming soon" placeholders) — self-employed registers a CORPORATE-type
// customer, Company Member Access an INDIVIDUAL-type one, each under its own KYC profile/questionnaire.
const ChooseAccountType = ({ userStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();
  // TRN-7453: the customer type being registered (not a shared boolean), so only the clicked card shows
  // the spinner and turns disabled — the other one keeps its normal look while the request is in flight
  // (handleRegister's isLoading guard makes a click on it a no-op).
  const [loadingType, setLoadingType] = useState(null);
  const isLoading = !!loadingType;
  const [error, setError] = useState(null);
  const [inviteCode, setInviteCode] = useState('');
  const [inviteCodeError, setInviteCodeError] = useState('');

  const availableAccounts = userStore?.userData?.availableAccounts;

  // At /verification this is always already populated by the app's normal bootstrap before DashBoard.js
  // ever reaches this component. At /registration/customer it's deliberately skipped by App.js's own
  // bootstrap effect (that route predates this reuse and still opts a mid-flow unauthenticated user out of
  // it) — so a logged-in user landing here directly (e.g. a full page refresh after the Header dropdown's
  // "Open an account") would otherwise see availableAccounts stay undefined. Reuses the same lightweight,
  // isolated fetch the old CustomerRegistrationTypeChoosing flow built for this exact edge case.
  useEffect(() => {
    if (isTokenValid() && !availableAccounts) {
      userStore.loadAvailableAccountsForRegistrationGate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reused verbatim from Registration/Account/index.js (§5.4 of the spec) — `isUserLoggedIn` drops out since
  // this screen is only ever reachable for an already-authenticated user (App.js's routing guard).
  const hasIndividualCustomer = useMemo(
    () => !!availableAccounts?.some((account) => account.type === CUSTOMER_TYPE.INDIVIDUAL),
    [availableAccounts]
  );
  const hasVerifiedCustomerAccount = useMemo(
    () => !!availableAccounts?.some((account) => account.status === USER_CUSTOMER_STATUSES.VERIFIED),
    [availableAccounts]
  );
  const shouldShowInviteCode = !(hasVerifiedCustomerAccount && !hasIndividualCustomer);
  // TRN-7456: only one Individual customer per user — once it exists the whole card is inert, the invite
  // code field included (without its placeholder), not just the Register button.
  const isInviteCodeDisabled = loadingType === KYC_PROFILE.INDIVIDUAL || hasIndividualCustomer;

  const handleChangeInviteCode = (e) => {
    setInviteCode(trimTextContent(e?.target?.value));
    setInviteCodeError('');
  };

  const handleBlurInviteCode = () => {
    if (inviteCode && inviteCode !== INVITE_CODE) {
      setInviteCodeError(i18n.getMessage('register.error.invalidInviteCode'));
    }
  };

  const handleRegister = async (kycProfile) => {
    if (isLoading) return;

    // Empty inviteCode isn't checked here — the button itself is disabled while it's empty (see the
    // AccountTypeCard's isDisabled prop below), so this can only ever be reached with a non-empty value.
    // Company Member Access is also an INDIVIDUAL-type customer, but only the plain Individual card itself
    // is gated by the invite code — Figma's Company Member Access card has no invite-code field at all.
    if (kycProfile === KYC_PROFILE.INDIVIDUAL && shouldShowInviteCode && inviteCode !== INVITE_CODE) {
      setInviteCodeError(i18n.getMessage('register.error.invalidInviteCode'));
      return;
    }

    setLoadingType(kycProfile);
    setError(null);

    // Same REGISTRATION_SUCCESS vs _ADDITIONAL split as the old SuccessAccountRegistration.js: "additional"
    // means the user already had accounts before this one was created.
    const isAdditionalCustomer = availableAccounts?.length > 0;
    let customer;

    try {
      customer = await userStore.registerCustomer(kycProfile);
    } catch (err) {
      setError(err);
      setLoadingType(null);
      return;
    }

    // The customer now exists on the backend, so from here on we navigate regardless of outcome: retrying
    // registerCustomer after this point would only fail with ..._ALREADY_REGISTERED, so a transient failure
    // of this best-effort refresh must not strand the user on a dead-end error screen offering to "register"
    // again.
    try {
      await userStore.loadUserData(null, true);
      await userStore.getAppState();
    } catch (err) {
      console.error('Post-registration data refresh failed:', err);
    }

    // Ported from the old SuccessAccountRegistration.js. The v2 endpoints return the created AccountDto, so its
    // number/type don't depend on the best-effort refresh above having updated localStorage.
    pushDataLayerEvent(
      isAdditionalCustomer ? DATA_LAYER_EVENTS.REGISTRATION_SUCCESS_ADDITIONAL : DATA_LAYER_EVENTS.REGISTRATION_SUCCESS,
      {
        user_id_internal: getInternalUserId(),
        customer_id: customer?.account_number || getCustomerId(),
        customer_type: customer?.type || getCustomerType()
      }
    );
    trackTelemetryEvent(TELEMETRY_EVENTS.REGISTRATION_CUSTOMER_SUCCESS, { customerNumber: customer?.account_number });

    // At /verification this is a same-path no-op (DashBoard.js already re-renders past its own
    // !userStore.userData?.account check once the above resolves, straight into Sumsub/Shufti/Manual -
    // spec §5.3). At /registration/customer it's required: that route is deliberately excluded from
    // App.js's auto-redirect-to-/verification guard (it predates this reuse and still needs to stay put
    // for a mid-flow unauthenticated user), so nothing else would ever navigate the user away from it.
    navigate(ROUTE_PATHS.VERIFICATION);
  };

  return (
    <div className={'account-type-page'}>
      <div className={'account-type-page__banner'}>
        <h1 className={'account-type-page__title'}>{i18n.getMessage('registration.accountType.title')}</h1>
        <p className={'account-type-page__subtitle'}>{i18n.getMessage('registration.accountType.subtitle')}</p>
      </div>

      <Alert type={'warning'} message={error && getErrorMessageForAlert(i18n, error)} />

      <div className={'account-type-page__grid'}>
        <AccountTypeCard
          Icon={BriefcaseIcon}
          titleKey={'registration.accountType.corporate.title'}
          descriptionKey={'registration.accountType.corporate.description'}
          onRegister={() => handleRegister(KYC_PROFILE.CORPORATE)}
          isLoading={loadingType === KYC_PROFILE.CORPORATE}
        />

        <AccountTypeCard
          Icon={UserOneIcon}
          titleKey={'registration.accountType.individual.title'}
          descriptionKey={'registration.accountType.individual.description'}
          onRegister={() => handleRegister(KYC_PROFILE.INDIVIDUAL)}
          isLoading={loadingType === KYC_PROFILE.INDIVIDUAL}
          isDisabled={hasIndividualCustomer || (shouldShowInviteCode && !inviteCode)}
        >
          {shouldShowInviteCode && (
            <Input
              className={'account-type-card__invite-code'}
              autoComplete={'off'}
              label={i18n.getMessage('registration.accountType.individual.inviteCodeLabel')}
              placeholder={
                isInviteCodeDisabled ? '' : i18n.getMessage('registration.accountType.individual.inviteCodePlaceholder')
              }
              name={'inviteCode'}
              value={inviteCode}
              error={inviteCodeError}
              onChange={handleChangeInviteCode}
              onBlur={handleBlurInviteCode}
              isDisabled={isInviteCodeDisabled}
            />
          )}
        </AccountTypeCard>

        <AccountTypeCard
          Icon={ComputerIcon}
          titleKey={'registration.accountType.selfEmployed.title'}
          descriptionKey={'registration.accountType.selfEmployed.description'}
          onRegister={() => handleRegister(KYC_PROFILE.SELF_EMPLOYED)}
          isLoading={loadingType === KYC_PROFILE.SELF_EMPLOYED}
        />

        <AccountTypeCard
          Icon={UserShieldIcon}
          titleKey={'registration.accountType.companyMember.title'}
          descriptionKey={'registration.accountType.companyMember.description'}
          onRegister={() => handleRegister(KYC_PROFILE.COMPANY_MEMBER)}
          isLoading={loadingType === KYC_PROFILE.COMPANY_MEMBER}
          // Same "only one Individual customer" rule as the plain Individual card above — this profile also
          // creates an INDIVIDUAL-type customer, so the backend rejects it the same way once the user already
          // has an individual-type customer, regardless of which of the two profiles created it.
          isDisabled={hasIndividualCustomer}
        />
      </div>
    </div>
  );
};

ChooseAccountType.propTypes = {
  userStore: MobXPropTypes.observableObject
};

export default inject('userStore')(observer(ChooseAccountType));
